// Fiók-kötés (affiliation) szerkesztő tiszta segédei (nem-komponens): form-értékek, alapértékek, a
// @atlas/contracts write-sémája (a resolverhez) és a lokalizált zod-hibatérkép. A minta a features/contacts/form.ts.
// A validálás forrás-igazsága a contract (AccountAffiliationUpsertBodySchema); külön fájl a komponenstől
// (react-refresh tisztaság). A `managerType` a form-oldalon NONE-sentinellel (a Radix Select üres-tiltása miatt).
import { AccountAffiliationUpsertBodySchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

/** A managerType select „nincs" sentinelje (a body-map null-ra alakítja). */
export const AFFILIATION_MANAGER_NONE = '__none__';

/** A form mező-értékei: identity (a picker id-je), kind (admin/contact), a kötés-flag-ek + a managerType (NONE-sentinel). */
export interface AffiliationFormValues {
  identityId: string;
  kind: 'admin' | 'contact';
  isPrimary: boolean;
  /**
   * A kötés régi „portál-hozzáférés" jelzője. A FELÜLETEN már NEM szerkeszthető: a jogot a portál-SZEREP adja
   * (fiókonként), és a bit abból SZÁRMAZIK (`szerep ≠ nincs → true`). Azért marad a modellben, mert a wire-body
   * hordozza, és a legacy/ETL-adat is ezt tölti — két vezérlő ugyanarra a dologra elcsúszó igazságot adna.
   */
  portalAccess: boolean;
  /** A partner-portál szerepe EZEN a fiókon (`portal-admin|member|viewer`), vagy a „nincs hozzáférése" sentinel. */
  portalRole: string;
  /** NONE-sentinel VAGY a managerType kód (csak admin-kötésnél értelmezett). */
  managerType: string;
}

/** A „nincs portál-hozzáférése" választó-érték (a `Select` nem kezel `null`-t). */
export const AFFILIATION_PORTAL_NONE = 'none';

export const EMPTY_AFFILIATION_FORM: AffiliationFormValues = {
  identityId: '',
  kind: 'admin',
  isPrimary: false,
  portalAccess: false,
  portalRole: AFFILIATION_PORTAL_NONE,
  managerType: AFFILIATION_MANAGER_NONE,
};

type UpsertBody = z.infer<typeof AccountAffiliationUpsertBodySchema>;

/**
 * Form-értékek → wire-body. A managerType CSAK admin-kötésnél + ha nem a NONE-sentinel → a kód, egyébként null.
 *
 * A `portalAccess` bit SZÁRMAZTATOTT (nincs saját kapcsolója): elsődleges kötésnél a szerver-invariáns miatt
 * mindig igaz, egyébként a választott portál-SZEREP dönti el. Egy vezérlő, egy igazság — két kapcsoló ugyanarra
 * a dologra elcsúszó állapotot adna (a bit igaz, de szerep nélkül a partner nem ér el semmit).
 */
export function toAffiliationBody(v: AffiliationFormValues): UpsertBody {
  const managerType =
    v.kind === 'admin' && v.managerType !== AFFILIATION_MANAGER_NONE
      ? (v.managerType as 'digital_marketing' | 'revenue_management')
      : null;
  return {
    identityId: v.identityId,
    kind: v.kind,
    isPrimary: v.isPrimary,
    portalAccess: v.isPrimary || v.portalRole !== AFFILIATION_PORTAL_NONE,
    managerType,
  };
}

/** Lokalizált zod-hibatérkép: az identity-kötelezőség (üres → uuid-hiba) a fő validált mező. */
export function buildAffiliationErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    if (issue.path[0] === 'identityId') {
      return { message: t('validation:accounts.affiliation.identity') };
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract UPSERT-sémából: a validálás a WIRE-alakon fut (a `toAffiliationBody` normalizálása után);
 * a hiba-pathok 1:1 a form-mezők. Sikerkor az EREDETI form-értékek mennek tovább (a body-t a mutáció építi).
 */
export function buildAffiliationResolver(t: TFunction): Resolver<AffiliationFormValues> {
  const base = zodResolver(AccountAffiliationUpsertBodySchema, {
    errorMap: buildAffiliationErrorMap(t),
  }) as unknown as Resolver<AffiliationFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toAffiliationBody(values) as unknown as AffiliationFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}
